import {
  DEFAULT_BASE_URL,
  DEFAULT_POLL_INTERVAL_MS,
  DEFAULT_POLL_TIMEOUT_MS,
  MAX_CONSECUTIVE_ERRORS,
} from "../constants";
import { clearElement } from "../core/dom";
import { Store } from "../core/state";
import { fetchJobStatus, submitLead } from "../api";
import { ApiError } from "../api/types";
import { saveLead } from "../services/storage";
import { injectFormStyles } from "../styles/inject";
import type { CapturedLead, FormError, InitOptions } from "../types";
import type { ResolvedBrand } from "./colors";
import { resolveBranding } from "./colors";
import type { Fields, FieldErrors } from "./validation";
import { normalizeDomain, validateAll, validateField } from "./validation";
import {
  renderComplete,
  renderError,
  renderIdle,
  renderPolling,
  renderSubmitting,
  type IdleRefs,
} from "./view";

type Phase = "idle" | "submitting" | "polling" | "complete" | "error";

type StoreShape = {
  phase: Phase;
  subStatus: "pending" | "researching" | "analysis_complete";
  elapsedMs: number;
  jobId: string | null;
  reportUrl: string | null;
  errors: FieldErrors;
  generalError: string | null;
  errorMessage: string;
  retryable: boolean;
};

const EMPTY_FIELDS: Fields = {
  company_name: "",
  company_domain: "",
  email: "",
};

export class FormController {
  private options: InitOptions;
  private brand: ResolvedBrand;
  private store: Store<StoreShape>;
  private fields: Fields;
  private targetEl: HTMLElement | null = null;
  private hostEl: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private idleRefs: IdleRefs | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private pollStartedAt: number = 0;
  private pollHiddenSince: number | null = null;
  private pollHiddenTotalMs = 0;
  private pollResumePending = false;
  private lastSubStatus: "pending" | "researching" | "analysis_complete" | null = null;
  private consecutiveErrors = 0;
  private destroyed = false;
  private unsubscribe: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;

  constructor(options: InitOptions) {
    this.options = options;
    this.brand = resolveBranding(options.branding);
    this.fields = { ...EMPTY_FIELDS };
    this.store = new Store<StoreShape>({
      phase: "idle",
      subStatus: "pending",
      elapsedMs: 0,
      jobId: null,
      reportUrl: null,
      errors: {},
      generalError: null,
      errorMessage: "",
      retryable: true,
    });
  }

  async init(): Promise<void> {
    if (typeof this.options.target === "string") {
      const found = document.querySelector(this.options.target);
      if (!found)
        throw new Error(
          `DPDPA report form: target element not found for selector "${this.options.target}"`
        );
      this.targetEl = found as HTMLElement;
    } else {
      this.targetEl = this.options.target;
    }

    this.hostEl = document.createElement("div");
    this.hostEl.setAttribute("data-redacto-dpdpa-report", "");
    this.hostEl.style.display = "block";
    this.targetEl.appendChild(this.hostEl);

    this.shadowRoot = this.hostEl.attachShadow({ mode: "open" });
    injectFormStyles(this.shadowRoot, this.brand);

    this.unsubscribe = this.store.subscribe((state, prev) => {
      if (state.phase !== prev.phase) this.render();
      else if (
        state.phase === "polling" &&
        state.subStatus !== prev.subStatus
      )
        this.render();
      else if (state.phase === "polling" && state.elapsedMs !== prev.elapsedMs)
        this.updateProgress();
    });

    this.visibilityHandler = () => this.handleVisibilityChange();
    document.addEventListener("visibilitychange", this.visibilityHandler);

    this.render();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearPolling();
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.store.destroy();
    if (this.hostEl && this.hostEl.parentNode)
      this.hostEl.parentNode.removeChild(this.hostEl);
    this.hostEl = null;
    this.shadowRoot = null;
    this.targetEl = null;
    this.idleRefs = null;
  }

  private render(): void {
    if (!this.shadowRoot) return;
    clearElement(this.shadowRoot as unknown as HTMLElement);
    // Re-inject styles — injectFormStyles is a no-op if already present.
    injectFormStyles(this.shadowRoot, this.brand);
    this.idleRefs = null;

    const s = this.store.getState();
    let node: HTMLElement;
    switch (s.phase) {
      case "idle":
        this.idleRefs = renderIdle(
          this.brand,
          this.fields,
          s.errors,
          s.generalError,
          {
            onInput: (field, value) => this.handleInput(field, value),
            onBlur: (field) => this.handleBlur(field),
            onSubmit: () => this.handleSubmit(),
          }
        );
        node = this.idleRefs.root;
        break;
      case "submitting":
        node = renderSubmitting(this.brand, this.fields);
        break;
      case "polling":
        node = renderPolling(this.brand, {
          companyName: this.fields.company_name,
          subStatus: s.subStatus,
          elapsedMs: s.elapsedMs,
          pollTimeoutMs: this.getPollTimeout(),
        });
        break;
      case "complete":
        node = renderComplete(this.brand, {
          companyName: this.fields.company_name,
          reportUrl: s.reportUrl || "#",
          onReset: () => this.resetToIdle(),
        });
        break;
      case "error":
        node = renderError(this.brand, {
          message: s.errorMessage,
          retryable: s.retryable,
          onRetry: () => this.submit(),
          onBack: () => this.backToIdle(s.generalError ?? s.errorMessage),
        });
        break;
    }
    this.shadowRoot.appendChild(node);

    if (s.phase === "idle" && this.idleRefs) {
      const firstErrorField = (Object.keys(s.errors) as (keyof Fields)[]).find(
        (f) => !!s.errors[f]
      );
      if (firstErrorField) this.idleRefs.fieldInputs[firstErrorField].focus();
    }
  }

  private updateProgress(): void {
    if (!this.shadowRoot) return;
    const bar = this.shadowRoot.querySelector<HTMLElement>(".dpdpa-progress-fill");
    const track = this.shadowRoot.querySelector<HTMLElement>(".dpdpa-progress");
    if (!bar) return;
    const pct = Math.min(
      100,
      Math.round((this.store.getState().elapsedMs / this.getPollTimeout()) * 100)
    );
    bar.style.width = `${pct}%`;
    if (track) track.setAttribute("aria-valuenow", String(pct));
  }

  private handleInput(field: keyof Fields, value: string): void {
    this.fields[field] = value;
    const prev = this.store.getState().errors[field];
    if (prev) {
      const next = { ...this.store.getState().errors };
      delete next[field];
      this.store.setState({ errors: next });
      if (this.idleRefs) this.clearFieldErrorSlot(field);
    }
  }

  private handleBlur(field: keyof Fields): void {
    const err = validateField(field, this.fields[field]);
    const next = { ...this.store.getState().errors };
    if (err) next[field] = err;
    else delete next[field];
    this.store.setState({ errors: next });
    this.updateFieldErrorSlot(field, err);
  }

  private updateFieldErrorSlot(field: keyof Fields, err: string | null): void {
    if (!this.idleRefs) return;
    const slot = this.idleRefs.fieldErrors[field];
    if (err) {
      slot.textContent = err;
      slot.style.display = "block";
    } else {
      slot.textContent = "";
      slot.style.display = "none";
    }
  }

  private clearFieldErrorSlot(field: keyof Fields): void {
    if (!this.idleRefs) return;
    const slot = this.idleRefs.fieldErrors[field];
    slot.textContent = "";
    slot.style.display = "none";
  }

  private handleSubmit(): void {
    const normalizedFields: Fields = {
      company_name: this.fields.company_name.trim(),
      company_domain: normalizeDomain(this.fields.company_domain),
      email: this.fields.email.trim(),
    };
    this.fields = normalizedFields;

    const errors = validateAll(normalizedFields);
    if (Object.keys(errors).length > 0) {
      this.store.setState({ errors, generalError: null });
      // Trigger a render so focus lands on first invalid (render handles focus).
      this.render();
      this.emitError("One or more fields need your attention.", "validation");
      return;
    }

    this.store.setState({ errors: {}, generalError: null });
    this.submit();
  }

  private async submit(): Promise<void> {
    const lead = this.makeLead(null);
    try {
      this.options.onSubmit?.({
        email: lead.email,
        company_name: lead.company_name,
        company_domain: lead.company_domain,
        timestamp: lead.timestamp,
      });
    } catch (err) {
      console.error("onSubmit handler threw:", err);
    }
    saveLead(lead);

    this.store.setState({ phase: "submitting" });

    try {
      const res = await submitLead({
        baseUrl: this.getBaseUrl(),
        body: {
          company_name: lead.company_name,
          company_domain: lead.company_domain,
        },
      });

      if (this.destroyed) return;

      if (res.status === "error") {
        this.showError(res.message || "We couldn't accept that submission.", false);
        this.emitError(res.message || "Submission rejected", "submit");
        return;
      }

      const acceptedLead: CapturedLead = { ...lead, job_id: res.job_id };
      saveLead(acceptedLead);
      try {
        this.options.onJobAccepted?.(res.job_id, acceptedLead);
      } catch (err) {
        console.error("onJobAccepted handler threw:", err);
      }

      this.startPolling(res.job_id);
    } catch (err) {
      if (this.destroyed) return;
      const apiErr = err as ApiError;
      const retryable = !apiErr.status || apiErr.status >= 500;
      this.showError(
        apiErr.message || "We couldn't reach our server. Please try again.",
        retryable
      );
      this.emitError(apiErr.message || "Submit failed", "submit", apiErr.status);
    }
  }

  private startPolling(jobId: string): void {
    this.clearPolling();
    this.pollStartedAt = Date.now();
    this.pollHiddenSince = null;
    this.pollHiddenTotalMs = 0;
    this.lastSubStatus = null;
    this.consecutiveErrors = 0;
    this.store.setState({
      phase: "polling",
      subStatus: "pending",
      elapsedMs: 0,
      jobId,
    });
    this.schedulePoll();
  }

  private schedulePoll(): void {
    if (this.destroyed) return;
    if (typeof document !== "undefined" && document.hidden) {
      if (this.pollHiddenSince === null) this.pollHiddenSince = Date.now();
      this.pollResumePending = true;
      return;
    }
    const interval = this.options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
    this.pollTimer = setTimeout(() => this.pollTick(), interval);
  }

  private getActiveElapsed(): number {
    return Date.now() - this.pollStartedAt - this.pollHiddenTotalMs;
  }

  private async pollTick(): Promise<void> {
    if (this.destroyed) return;
    const jobId = this.store.getState().jobId;
    if (!jobId) return;

    const activeElapsed = this.getActiveElapsed();
    if (activeElapsed >= this.getPollTimeout()) {
      this.showError(
        "Report generation is taking longer than expected. Our team has been notified.",
        false
      );
      this.emitError("Polling timed out", "timeout");
      return;
    }

    try {
      const res = await fetchJobStatus({ baseUrl: this.getBaseUrl(), jobId });
      if (this.destroyed) return;
      this.consecutiveErrors = 0;

      if ("status" in res && res.status === "error") {
        this.showError(res.message || "Report generation failed.", true);
        this.emitError(res.message || "Job errored", "poll");
        return;
      }

      if (res.status === "complete" && "report_url" in res && res.report_url) {
        const acceptedLead: CapturedLead = this.makeLead(jobId);
        saveLead(acceptedLead);
        this.store.setState({
          phase: "complete",
          reportUrl: res.report_url,
        });
        try {
          this.options.onComplete?.(res.report_url, acceptedLead);
        } catch (err) {
          console.error("onComplete handler threw:", err);
        }
        return;
      }

      const sub = res.status as "pending" | "researching" | "analysis_complete";
      if (sub !== this.lastSubStatus) {
        this.lastSubStatus = sub;
        try {
          this.options.onStatusChange?.(sub);
        } catch (err) {
          console.error("onStatusChange handler threw:", err);
        }
      }
      this.store.setState({
        phase: "polling",
        subStatus: sub,
        elapsedMs: activeElapsed,
      });
      this.schedulePoll();
    } catch (err) {
      if (this.destroyed) return;
      this.consecutiveErrors++;
      if (this.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
        this.showError(
          "We can't reach our server right now. Please try again.",
          true
        );
        this.emitError(
          err instanceof Error ? err.message : "Poll failed",
          "poll"
        );
        return;
      }
      this.schedulePoll();
    }
  }

  private clearPolling(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.pollResumePending = false;
  }

  private handleVisibilityChange(): void {
    if (this.destroyed) return;
    if (document.hidden) {
      if (
        this.store.getState().phase === "polling" &&
        this.pollHiddenSince === null
      ) {
        this.pollHiddenSince = Date.now();
      }
      return;
    }
    if (this.pollHiddenSince !== null) {
      this.pollHiddenTotalMs += Date.now() - this.pollHiddenSince;
      this.pollHiddenSince = null;
    }
    if (this.pollResumePending && this.store.getState().phase === "polling") {
      this.pollResumePending = false;
      this.pollTick();
    }
  }

  private showError(message: string, retryable: boolean): void {
    this.store.setState({
      phase: "error",
      errorMessage: message,
      retryable,
    });
  }

  private resetToIdle(): void {
    this.fields = { ...EMPTY_FIELDS };
    this.lastSubStatus = null;
    this.store.setState({
      phase: "idle",
      errors: {},
      generalError: null,
      errorMessage: "",
      reportUrl: null,
      jobId: null,
      subStatus: "pending",
      elapsedMs: 0,
    });
  }

  private backToIdle(generalError: string | null): void {
    this.store.setState({
      phase: "idle",
      errors: {},
      generalError,
      errorMessage: "",
    });
  }

  private emitError(message: string, phase: FormError["phase"], status?: number): void {
    try {
      const err = Object.assign(new Error(message), { phase, status }) as FormError;
      this.options.onError?.(err);
    } catch (e) {
      console.error("onError handler threw:", e);
    }
  }

  private makeLead(jobId: string | null): CapturedLead {
    return {
      email: this.fields.email,
      company_name: this.fields.company_name,
      company_domain: this.fields.company_domain,
      job_id: jobId,
      timestamp: new Date().toISOString(),
    };
  }

  private getBaseUrl(): string {
    return (this.options.baseUrl || DEFAULT_BASE_URL).replace(/\/$/, "");
  }

  private getPollTimeout(): number {
    return this.options.pollTimeoutMs ?? DEFAULT_POLL_TIMEOUT_MS;
  }
}
